/**
 * What sits behind one statement's numbers (SYR-111 v4): the reasons each
 * assistant gave for its answer, and what every page read says on it, with
 * the page's own words. Shown when a row of the facts or beliefs table is
 * expanded, on the brand's page and in the admin preview alike.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { Tooltip } from 'antd';
import { THEME } from '../../config/theme';
import { IModelBeliefsPageReading } from '../../../../services/nestjs-base-api';
import { ANCHORS } from './beliefCells';

interface Props {
  reasons: { label: string; text: string }[];
  readings: IModelBeliefsPageReading[];
  // Whether the read's searched view is the pages, so an empty readings list
  // is a finding ("no page speaks to this") rather than nothing to show.
  pagesMode: boolean;
}

const head: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: THEME.textSecondary,
  marginBottom: 4,
};

export const StatementDetail: React.FunctionComponent<Props> = ({ reasons, readings, pagesMode }) => {
  const intl = useIntl();
  if (reasons.length === 0 && readings.length === 0 && !pagesMode) return null;

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: '80ch' }}>
      {reasons.length > 0 && (
        <div>
          <div style={head}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.detail.reasons',
              defaultMessage: 'Why the models think so, in their own words',
            })}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {reasons.map((reason, i) => (
              <div key={i} style={{ fontSize: 13, lineHeight: 1.5, color: THEME.textBase, minWidth: 0 }}>
                <strong style={{ marginRight: 6 }}>{reason.label}</strong>
                <span style={{ color: THEME.textSecondary }}>{reason.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {pagesMode && (
        <div>
          <div style={head}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.detail.pages',
              defaultMessage: 'What the pages say on this',
            })}
          </div>
          {readings.length === 0 ? (
            <div style={{ fontSize: 13, color: THEME.textMuted }}>
              {intl.formatMessage({
                id: 'marketerView.modelBeliefs.detail.noPages',
                defaultMessage: 'None of the pages read speaks to this statement.',
              })}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {readings.map((reading, i) => (
                <div
                  key={`${reading.url}-${i}`}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, minWidth: 0 }}
                >
                  <Tooltip title={ANCHORS[Math.round(reading.score)] ?? ''}>
                    <span
                      style={{
                        flex: '0 0 auto',
                        minWidth: 26,
                        textAlign: 'center',
                        fontWeight: 700,
                        borderRadius: 6,
                        padding: '1px 6px',
                        background: THEME.bgChip,
                        color: THEME.textBase,
                      }}
                    >
                      {reading.score}
                    </span>
                  </Tooltip>
                  <div style={{ minWidth: 0 }}>
                    <a href={reading.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                      {reading.domain}
                    </a>
                    <span style={{ color: THEME.textMuted, fontSize: 12, marginLeft: 6 }}>
                      {intl.formatMessage(
                        {
                          id: 'marketerView.modelBeliefs.detail.readIn',
                          defaultMessage: 'read in {n} answers',
                        },
                        { n: reading.answers },
                      )}
                    </span>
                    <div style={{ fontStyle: 'italic', color: THEME.textBase, lineHeight: 1.5 }}>“{reading.quote}”</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
